import React from "react";
import { ModalShell } from "../../../atoms/ModalShell";
import { useTranslation } from "react-i18next";
import parse, { domToReact, HTMLReactParserOptions } from "html-react-parser";
import { VarList, VarRow } from "../../../common/VarList";

type WebhookIntegrationGuideModalProps = {
  show: boolean;
  onClose: () => void;
};

const parseOptions: HTMLReactParserOptions = {
  replace: (node: any) => {
    if (node.type === "tag" && node.name === "code") {
      return (
        <code className="text-xs bg-gray-100 text-red-600 px-1 py-0.5 rounded">
          {domToReact(node.children, parseOptions)}
        </code>
      );
    }
    return undefined;
  },
};

const code = (text: string) => parse(`<code>${text}</code>`, parseOptions);

const WebhookIntegrationGuideModal = ({
  show,
  onClose,
}: WebhookIntegrationGuideModalProps) => {
  const { t } = useTranslation();
  const prefix = "dashboard.entities.entityPreferences.webhookGuide";
  const tHtml = (key: string) =>
    parse(t(`${prefix}.${key}`) as string, parseOptions);

  const metadataLabel = t(`${prefix}.tableHeaderMetadata`) as string;
  const whenItFiresLabel = t(`${prefix}.tableHeaderWhenItFires`) as string;
  const whenItIsSentLabel = t(`${prefix}.tableHeaderWhenItIsSent`) as string;

  return (
    <ModalShell
      show={show}
      onClose={onClose}
      size="4xl"
      title={t(`${prefix}.title`)}
    >
      <div className="space-y-6 text-sm text-gray-600 leading-relaxed">
        <div>
          <h4 className="text-gray-800 font-semibold mb-1">
            {t(`${prefix}.overviewTitle`)}
          </h4>
          <p className="mb-2">{tHtml("overviewIntro")}</p>
          <ul className="space-y-1.5 pl-1">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("overviewPaymod")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("overviewSum")}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-gray-800 font-semibold mb-2">
            {t(`${prefix}.deliveryTitle`)}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-gray-200 rounded">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50 w-1/3">
                    {t(`${prefix}.deliveryMethod`)}
                  </td>
                  <td className="px-3 py-2">POST</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.deliveryContentType`)}
                  </td>
                  <td className="px-3 py-2">application/json</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.deliveryTimeout`)}
                  </td>
                  <td className="px-3 py-2">2000 ms</td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.deliveryExpectedResponse`)}
                  </td>
                  <td className="px-3 py-2">
                    {tHtml("deliveryExpectedResponseValue")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-gray-500">{tHtml("deliveryNote")}</p>
        </div>

        <div>
          <h4 className="text-gray-800 font-semibold mb-1">
            {t(`${prefix}.payloadTitle`)}
          </h4>
          <p className="mb-2">{t(`${prefix}.payloadIntro`)}</p>
          <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto">
            <pre className="text-xs text-green-300">
              {`{
  "entity_identification": "string",
  "user_identification": "string",
  "event_type": "SUM | PAYMOD",
  "event": "<event enum, see tables below>",
  "event_timestamp": "2025-05-19T12:34:56.789Z",
  "metadata": { }
}`}
            </pre>
          </div>
          <ul className="mt-3 space-y-1.5 pl-1 text-xs">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldEntityIdentification")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldUserIdentification")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldEventType")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldEvent")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldEventTimestamp")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("payloadFieldMetadata")}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-gray-800 font-semibold mb-2">
            {tHtml("sumTitle")}
          </h4>
          <VarList>
            <VarRow
              placeholder="USER_CREATED"
              description={t(`${prefix}.sumUserCreated`) as string}
              example={code("null")}
              descriptionLabel={whenItFiresLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="USER_EMAIL_VALIDATION_SUCCEEDED"
              description={t(`${prefix}.sumUserEmailValidated`) as string}
              example={code("null")}
              descriptionLabel={whenItFiresLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="USER_BANNED"
              description={t(`${prefix}.sumUserBanned`) as string}
              example={code("null")}
              descriptionLabel={whenItFiresLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="USER_UNBANNED"
              description={t(`${prefix}.sumUserUnbanned`) as string}
              example={code("null")}
              descriptionLabel={whenItFiresLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="USER_DELETED"
              description={t(`${prefix}.sumUserDeleted`) as string}
              example={code("null")}
              descriptionLabel={whenItFiresLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
          </VarList>
        </div>

        <div>
          <h4 className="text-gray-800 font-semibold mb-2">
            {tHtml("paymodTitle")}
          </h4>
          <VarList>
            <VarRow
              placeholder="PAYMOD_ACTIVE"
              description={t(`${prefix}.paymodActive`) as string}
              example={code(`{ "plan_code": "string" }`)}
              descriptionLabel={whenItIsSentLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="PAYMOD_SUCCEEDED"
              description={t(`${prefix}.paymodSucceeded`) as string}
              example={code(`{ "plan_code": "string" }`)}
              descriptionLabel={whenItIsSentLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="PAYMOD_ERROR"
              description={t(`${prefix}.paymodError`) as string}
              example={code(`{ "plan_code": "string" }`)}
              descriptionLabel={whenItIsSentLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
            <VarRow
              placeholder="PAYMOD_CANCELED"
              description={t(`${prefix}.paymodCanceled`) as string}
              example={code(`{ "plan_code": "string" }`)}
              descriptionLabel={whenItIsSentLabel}
              exampleLabel={metadataLabel}
              stackLabels
            />
          </VarList>
        </div>

        <div>
          <h4 className="text-gray-800 font-semibold mb-2">
            {t(`${prefix}.examplesTitle`)}
          </h4>

          <p className="text-gray-500 text-xs mb-1">
            {t(`${prefix}.exampleSumLabel`)}
          </p>
          <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-3">
            <pre className="text-xs text-green-300">
              {`{
  "entity_identification": "1A2B3C4D5E6F7G8H9I0J",
  "user_identification": "678D7Y9L4MLBF4QBZFTR",
  "event_type": "SUM",
  "event": "USER_CREATED",
  "event_timestamp": "2025-05-19T10:15:42.123Z",
  "metadata": null
}`}
            </pre>
          </div>

          <p className="text-gray-500 text-xs mb-1">
            {t(`${prefix}.examplePaymodLabel`)}
          </p>
          <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto">
            <pre className="text-xs text-green-300">
              {`{
  "entity_identification": "1A2B3C4D5E6F7G8H9I0J",
  "user_identification": "678D7Y9L4MLBF4QBZFTR",
  "event_type": "PAYMOD",
  "event": "PAYMOD_ACTIVE",
  "event_timestamp": "2025-05-19T10:16:05.884Z",
  "metadata": { "plan_code": "PRO_MONTHLY" }
}`}
            </pre>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-gray-800 font-semibold mb-2">
            {t(`${prefix}.securityTitle`)}
          </h4>
          <p className="mb-3">{tHtml("securityIntro")}</p>

          <h5 className="text-gray-700 font-semibold text-sm mb-1">
            {t(`${prefix}.securityHeaderTitle`)}
          </h5>
          <p className="mb-2">{tHtml("securityHeaderBody")}</p>
          <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-3">
            <pre className="text-xs text-green-300">
              {`X-Sum-Signature: t=1713541234,v1=a1b2c3d4e5f6...`}
            </pre>
          </div>
          <ul className="space-y-1.5 pl-1 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityFieldT")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityFieldV1")}</span>
            </li>
          </ul>

          <h5 className="text-gray-700 font-semibold text-sm mb-1">
            {t(`${prefix}.securitySignedTitle`)}
          </h5>
          <p className="mb-2">{tHtml("securitySignedBody")}</p>
          <div className="p-2.5 bg-gray-900 rounded-md overflow-x-auto mb-3">
            <pre className="text-xs text-green-300">
              {`<timestamp>.<raw_json_body>`}
            </pre>
          </div>
          <p className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            {tHtml("securityRawBodyWarning")}
          </p>

          <h5 className="text-gray-700 font-semibold text-sm mb-1">
            {t(`${prefix}.securityStepsTitle`)}
          </h5>
          <ul className="space-y-1.5 pl-1 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityStep1")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityStep2")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityStep3")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-0.5 shrink-0">•</span>
              <span>{tHtml("securityStep4")}</span>
            </li>
          </ul>

          <h5 className="text-gray-700 font-semibold text-sm mb-1">
            {t(`${prefix}.securityRotationTitle`)}
          </h5>
          <p className="mb-4">{tHtml("securityRotationBody")}</p>

          <h5 className="text-gray-700 font-semibold text-sm mb-2">
            {t(`${prefix}.securityQuickRefTitle`)}
          </h5>
          <div className="overflow-x-auto mb-2">
            <table className="w-full text-xs border border-gray-200 rounded">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50 w-1/2">
                    {t(`${prefix}.quickRefHeader`)}
                  </td>
                  <td className="px-3 py-2">{code("X-Sum-Signature")}</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.quickRefAlgorithm`)}
                  </td>
                  <td className="px-3 py-2">HMAC-SHA256</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.quickRefPrefix`)}
                  </td>
                  <td className="px-3 py-2">{code("whsec_")}</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.quickRefTimestamp`)}
                  </td>
                  <td className="px-3 py-2">
                    {t(`${prefix}.quickRefTimestampValue`)}
                  </td>
                </tr>
                <tr>
                  <td className="px-3 py-2 font-semibold bg-gray-50">
                    {t(`${prefix}.quickRefTolerance`)}
                  </td>
                  <td className="px-3 py-2">
                    {t(`${prefix}.quickRefToleranceValue`)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            {tHtml("securityCompatibilityNote")}
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-gray-800 font-semibold mb-2">
            {t(`${prefix}.notesTitle`)}
          </h4>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
              <span>{tHtml("notesAlwaysRespond")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
              <span>{t(`${prefix}.notesQuickly`)}</span>
            </li>
          </ul>
        </div>
      </div>
    </ModalShell>
  );
};

export default WebhookIntegrationGuideModal;
